import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { Page, PageMetadata } from '@tran-monorepo/interfaces';
import { Unit } from '@tran-monorepo/interfaces/applications';
import { EMPTY, Observable } from 'rxjs';
import { catchError, expand, finalize, takeWhile, tap } from 'rxjs/operators';
import { UnitsService } from '../../services';
import { LoadAllUnits, LoadMoreUnits, LoadUnits } from './units.actions';

export interface UnitsStateModel {
    loading: boolean;
    units: Unit[];
    metadata: PageMetadata | null;
    pageSize: number;
}

@State<UnitsStateModel>({
    name: 'units',
    defaults: {
        loading: false,
        units: [],
        metadata: null,
        pageSize: 20
    }
})
@Injectable()
export class UnitsState {
    private readonly unitsService = inject(UnitsService);

    @Selector()
    static units(state: UnitsStateModel): Unit[] {
        return state.units;
    }

    @Selector()
    static loading(state: UnitsStateModel): boolean {
        return state.loading;
    }

    @Selector()
    static metadata(state: UnitsStateModel): PageMetadata | null {
        return state.metadata;
    }

    @Action(LoadUnits)
    load(ctx: StateContext<UnitsStateModel>, { payload: { pageSize } }: LoadUnits): Observable<Page<Unit>> {
        ctx.patchState({ loading: true, pageSize });

        return this.unitsService.getAll({ page: 0, pageSize }).pipe(
            tap(({ items: units, metadata }) =>
                ctx.patchState({
                    units,
                    metadata
                })
            ),
            catchError(() => {
                ctx.patchState({ units: [], metadata: null });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    @Action(LoadMoreUnits)
    loadMore(ctx: StateContext<UnitsStateModel>): Observable<Page<Unit>> | void {
        const { loading, metadata, pageSize } = ctx.getState();

        if (loading || !metadata?.hasNextPage) {
            return;
        }

        const nextPage = (metadata?.currentPage ?? 0) + 1;
        ctx.patchState({ loading: true });

        return this.unitsService.getAll({ page: nextPage, pageSize }).pipe(
            tap(({ items: newUnits, metadata }) =>
                ctx.setState(
                    patch<UnitsStateModel>({
                        units: append<Unit>(newUnits),
                        metadata
                    })
                )
            ),
            catchError(() => {
                ctx.patchState({ loading: false });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    @Action(LoadAllUnits)
    loadAll(ctx: StateContext<UnitsStateModel>): Observable<void> {
        ctx.patchState({ loading: true, units: [], metadata: null });

        return ctx.dispatch(new LoadUnits({ pageSize: 100 })).pipe(
            expand(() => {
                const { metadata } = ctx.getState();
                return metadata?.hasNextPage ? ctx.dispatch(new LoadMoreUnits()) : EMPTY;
            }),
            takeWhile(() => {
                const { metadata } = ctx.getState();
                return metadata?.hasNextPage ?? false;
            }, true),
            catchError(() => {
                ctx.patchState({ loading: false, units: [], metadata: null });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }
}
